import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { PropertyStoreService } from './property-store.service';
import { PropertyService } from '../property.service';
import { User } from '../../domain/user';
import { UserService } from '../user.service';
import { Group } from '../../domain/group';
import { Role } from '../../domain/role';
import { LoadingOverlayStoreService } from './loading-overlay-store.service';
import { LoadingState } from '../../domain/loading-state';
import { IRefreshable } from './irefreshable';
import { DataStoreService } from '../data-store.service';
import { AuthService, IdToken } from '@auth0/auth0-angular';
import { UserQueryRequest } from '../request/user-query-request';

@Injectable({
   providedIn: 'root'
})
export class UserStoreService implements IRefreshable {
   private _users: BehaviorSubject<User[]> = new BehaviorSubject(new Array<User>());

   public readonly users: Observable<User[]> = this._users.asObservable();

   private currentPropertyId: number | undefined;

   private _currentUser: ReplaySubject<User> = new ReplaySubject(1, Infinity);

   public readonly currentUser: Observable<User> = this._currentUser.asObservable();

   private currentUserBase?: User;

   constructor(
      private propertyStore: PropertyStoreService,
      private propertyService: PropertyService,
      private userService: UserService,
      private loadingOverlayStore: LoadingOverlayStoreService,
      private dataStoreService: DataStoreService,
      public auth: AuthService
   ) {
      this.propertyStore.currentProperty.subscribe((property) => {
         this.currentPropertyId = property.id;
         this.updateData();
         this.refreshCurrentUserForProperty();
      });
      this.dataStoreService.refreshTaskDataSubject.subscribe((value) => {
         if (value.Users || value.Groups) {
            this.updateData();
         }
      });
      this.auth.idTokenClaims$.subscribe((c: IdToken | undefined | null) => {
         if (c?.sid) {
            const userRequest = new UserQueryRequest();
            userRequest.currentUser = true;
            this.userService.queryUser(userRequest).subscribe((res) => {
               this.currentUserBase = res.user;
               this._currentUser.next(res.user);
               this.refreshCurrentUserForProperty();
            });
         }
      });
   }

   updateData() {
      if (this.currentPropertyId) {
         const loadingState = new LoadingState();
         loadingState.Users = true;
         this.loadingOverlayStore.setLoadingState(loadingState);
         this.propertyService.getUsers(this.currentPropertyId).subscribe((users) => {
            const loadingState = new LoadingState();
            loadingState.Users = false;
            this.loadingOverlayStore.setLoadingState(loadingState);
            this._users.next(users);
         });
      }
   }

   updateEnabled(userId: string, value: boolean): Observable<User> {
      return this.userService.updateEnabled(userId, value);
   }

   addRoles(userId: string, roles: Role[]): Observable<User> {
      if (this.currentPropertyId) {
         return this.userService.addRole(userId, this.currentPropertyId, roles);
      }
      return new Observable<User>();
   }

   addGroups(userId: string, groups: Group[]): Observable<User> {
      if (this.currentPropertyId) {
         return this.userService.addGroup(userId, this.currentPropertyId, groups);
      }
      return new Observable<User>();
   }

   deleteRole(userId: string, role: Role): Observable<User> {
      if (this.currentPropertyId) {
         return this.userService.deleteRole(userId, this.currentPropertyId, role);
      }
      return new Observable<User>();
   }

   deleteGroup(userId: string, group: Group): Observable<User> {
      if (this.currentPropertyId) {
         return this.userService.deleteGroup(userId, this.currentPropertyId, group);
      }
      return new Observable<User>();
   }

   private refreshCurrentUserForProperty(): void {
      if (!this.currentPropertyId) return;

      const authId = this.currentUserBase?.authId;
      if (!authId) return;

      this.userService
         .getUserWithGroupsForProperty(authId, this.currentPropertyId)
         .subscribe({
            next: (u) => {
               this.currentUserBase = u;
               this._currentUser.next(u);
            },
            error: (e) => {
               console.error(e);
               if (this.currentUserBase) this._currentUser.next(this.currentUserBase);
            }
         });
   }

   refreshStore(): void {
      this.updateData();
   }
}
